import { SchoolTeamGenderEnum, UserRoleEnum } from '@spikemark/rest-api';
import { Entitlement } from '@spikemark/user-api';

export enum EntitlementType {
  /** Coach Portal - Entry */
  CoachPortalAccess = 'COACH_PORTAL_ACCESS',
  /** Clippd App */
  Clippd = 'CLIPPD_ACCESS',
  /** Live Scoring 'Credit' */
  LiveScoringTournament = 'LIVE_SCORING_TOURNAMENT',
  /** Coach Portal - Pro */
  ProAnalytics = 'PRO_ANALYTICS',
  /** Coach Portal - Standard */
  QualifyingLiveScoring = 'QUALIFYING_LIVE_SCORING',
}

export const PACKAGE_ENTITLEMENTS: Readonly<Set<EntitlementType>> = new Set([
  EntitlementType.CoachPortalAccess,
  EntitlementType.ProAnalytics,
  EntitlementType.QualifyingLiveScoring,
]);

export enum SpikemarkAuthRole {
  Admins = 'Admins',
  Coaches = 'Coaches',
  Committee = 'Committee',
  Players = 'Players',
  Scorers = 'Scorers',
}

export enum SpikemarkResource {
  Tournaments = 'Tournaments',
  Teams = 'Teams',
  Players = 'Players',
  Users = 'Users',
  Clubs = 'Clubs',
  CommitteeVisualisations = 'CommitteeVisualisations',
  Rankings = 'Rankings',
  Analytics = 'Analytics',
}

export type TeamSessionData = {
  orgId: string;
  schoolId: string;
  role: SpikemarkAuthRole;
  entitlements: Array<{
    id: string;
    entitlement_id: string;
    status: 'PENDING' | 'ACTIVE' | 'DEFERRED' | 'EXPIRED' | 'CANCELLED' | 'AVAILABLE';
    paymentUserId: string;
    entitlement: EntitlementType;
  }>;
  gender: SchoolTeamGenderEnum;
};

export type OrgMap = Record<string, TeamSessionData>;

export type SpikemarkUser = {
  name?: string | null;
  image?: string | null;
  email: string;
  userId: string;
  tableauToken?: string;
  schools: OrgMap;
  hosts?: string[];
  userRole?: UserRoleEnum;
  isAdmin: boolean;
};

export enum AccessScope {
  None,
  Own,
  Any,
}

export type AnyResource = 'any';
export type CRUDAction = 'create' | 'read' | 'update' | 'delete' | 'any';

// All possible actions including CRUD and custom actions
export type AllActions =
  | CRUDAction
  | 'roster'
  | 'enter'
  | 'withdraw'
  | 'save-results'
  | 'publish-results'
  | 'publish-feed';

export type AccessGrant<T = unknown> = {
  granted: boolean;
  restrict?: string[];
  refine?: (data: T) => boolean;
};

export type UserAccessMap = Partial<
  Record<
    AnyResource | SpikemarkResource,
    Partial<Record<AllActions, Partial<Record<AccessScope, AccessGrant>>>>
  >
>;

export type AuthRequest = {
  username?: string;
  password?: string;
  sub?: string;
  refreshToken?: string;
  accessCode?: string;
};

export type AuthWithPasswordRequest = Pick<AuthRequest, 'username' | 'password'>;
export type AuthWithAccessCodeRequest = Pick<AuthRequest, 'accessCode'>;
export type AuthWithRefreshTokenRequest = Pick<AuthRequest, 'sub' | 'refreshToken'>;

export type AuthSignOutReason =
  | 'tournament-does-not-exist'
  | 'cannot-refresh-token'
  | 'authentication-failed'
  | 'silent-login-failed-no-auth'
  | 'user-initiated'
  | 'user-initiated-tournament-complete';

export type AuthResponse = {
  supabaseToken: string;
  supabaseProfile: SupabaseContext;
} & (
  | {
      loginType: 'code';
    }
  | {
      loginType: 'user';
      user: SpikemarkUser;
      sub: string;
      idToken: string;
      accessToken: string;
      refreshToken: string;
      expiresIn: number;
    }
);

export type SupabaseContext = {
  teamIds?: number[];
  hostIds?: string[];
  roles: SpikemarkAuthRole[];
  tournamentId?: number;
  userRole?: UserRoleEnum;
};

export type SupabaseTokenPayload = {
  roles: SpikemarkAuthRole[];
  hosts?: string[];
  schools?: { id: string }[];
  userRole?: UserRoleEnum;
};
